import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React from 'react'
import { useFormContext } from 'react-hook-form'

import Button from 'components/Button'
import FieldError from 'components/fields/FieldError'
import Fieldset from 'components/fields/Fieldset'
import FormRow from 'components/fields/FormRow'
import Label from 'components/fields/Label'
import { AddImageIcon } from 'components/icons'
import { useTracking } from 'hooks/useTracking'
import { uploadFile } from 'lib/api'
import { EVENT_NAMES } from 'lib/tracking'
import { FileKind } from 'types/file'

const HiddenInput = styled.input`
	display: none;
`

const UploadedImage = styled(Image)`
	height: 34px;
	width: 34px;
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	margin-right: ${({ theme }) => theme.spaces.space4};
`

const UploadFormRow = styled(FormRow)<{ compact?: boolean }>`
	margin-bottom: 0;

	${({ compact }) =>
		compact &&
		css`
			button {
				width: 77px;
			}
		`};
`

export const loadingPulse = keyframes`
	from {
		opacity: 0.2;
		color: transparent;
	}

	to {
		opacity: 1;
		color: inherit;
	}
`

const ImagePlaceholder = styled.span<{ isLoading: boolean }>`
	height: 34px;
	width: 34px;
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	background-color: ${({ theme }) => theme.colors.light.borderColor};
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: ${({ theme }) => theme.spaces.space4};
	animation: ${({ isLoading }) => (isLoading ? loadingPulse : 'none')} 800ms ease-in-out infinite alternate;
`

type Props = {
	kind: FileKind
	fieldName: string
	label: string
	alt: string
	emptyFieldValue: string | null
	compact?: boolean
}

export const useFileUpload = (kind: FileKind, fieldName: string, emptyFieldValue: string | null) => {
	const router = useRouter()
	const track = useTracking()
	const fileInputRef = React.useRef<HTMLInputElement>(null)
	const [isImageUploading, setIsImageUploading] = React.useState(false)
	const { setValue, setError, clearErrors, watch, formState } = useFormContext()
	const fileUrl = watch(fieldName)
	const error = formState.errors[fieldName]?.message as string
	const routerPath = router.asPath

	const handleFileChange = React.useCallback(
		async (
			event: React.ChangeEvent<HTMLInputElement>,
			handleError: (message: string) => void = (message: string) => setError(fieldName, { message }),
			handleClearErrors: () => void = () => clearErrors(fieldName),
		) => {
			const inputFiles: File[] = event.target.files ? Array.from(event.target.files) : []

			if (inputFiles.length === 0) {
				return
			}

			const file = inputFiles[0]

			if (file.size > 1024 * 1024) {
				handleError('Maximum file size is 1MB')
				return
			}

			handleClearErrors()
			setIsImageUploading(true)

			try {
				const response = await uploadFile('img', kind, file)

				setValue(fieldName, response.url, { shouldValidate: true, shouldDirty: true })
				track(EVENT_NAMES.FILE_UPLOAD_SUCCESS, { source: routerPath, kind })
			} catch {
				handleError('Something went wrong, please try again')
				track(EVENT_NAMES.FILE_UPLOAD_FAILURE, { source: routerPath, kind })
			} finally {
				if (fileInputRef.current) {
					fileInputRef.current.value = ''
				}
				setIsImageUploading(false)
			}
		},
		[clearErrors, fieldName, kind, routerPath, setError, setValue, track],
	)

	const onRemoveClick = React.useCallback(() => {
		setValue(fieldName, emptyFieldValue, { shouldValidate: true, shouldDirty: true })
	}, [fieldName, emptyFieldValue, setValue])

	const onUploadClick = React.useCallback(() => {
		fileInputRef.current?.click()
	}, [])

	return {
		fileUrl,
		onRemoveClick,
		onUploadClick,
		handleFileChange,
		error,
		isImageUploading,
		fileInputRef,
	}
}

const ImageUpload = ({ kind, fieldName, label, alt, compact, emptyFieldValue }: Props) => {
	const fileInputId = React.useId()
	const { fileUrl, onRemoveClick, onUploadClick, handleFileChange, error, isImageUploading, fileInputRef } =
		useFileUpload(kind, fieldName, emptyFieldValue)

	const actionButton = fileUrl ? (
		<Button type="button" onClick={onRemoveClick} mediumDesktop compact={compact}>
			Remove
		</Button>
	) : (
		<Button type="button" onClick={onUploadClick} mediumDesktop compact={compact}>
			Upload
		</Button>
	)

	return (
		<Fieldset data-testid={`${kind}-image-upload`}>
			<Label htmlFor={fileInputId}>{label}</Label>
			<UploadFormRow compact={compact}>
				{fileUrl && !isImageUploading ? (
					<UploadedImage src={fileUrl} alt={alt} width={50} height={50} />
				) : (
					<ImagePlaceholder isLoading={isImageUploading} data-testid="image-placeholder">
						<AddImageIcon />
					</ImagePlaceholder>
				)}
				{isImageUploading ? <span>Uploading...</span> : actionButton}
				<HiddenInput
					type="file"
					id={fileInputId}
					accept="image/png,image/jpeg"
					ref={fileInputRef}
					onChange={handleFileChange}
				/>
				{error && <FieldError>{error}</FieldError>}
			</UploadFormRow>
		</Fieldset>
	)
}

export default ImageUpload
